import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import config from '../config';
import usePWAInstall from '../hooks/usePWAInstall';
import usePremiumPlan from '../hooks/usePremiumPlan';
import InstallInstructionsModal from './InstallInstructionsModal';
import PremiumBadge from './PremiumBadge';
import '../styles/SideNavbar.css';

function SideNavbar({ user, onProfileClick, notificationCount = 0, groupNotificationCount = 0, isMobile = false, showChat = false }) {
  const navigate = useNavigate();
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showInstallHelp, setShowInstallHelp] = useState(false);
  const { canInstall, isInstalled, isIOS, promptInstall } = usePWAInstall();
  const isPremium = usePremiumPlan(user?.id);

  const activeTab = window.location.pathname.replace('/', '') || 'chats';

  const navItems = [
    {
      id: 'chats',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
        </svg>
      ),
      label: 'Chats'
    },
    {
      id: 'contacts',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
          <circle cx="9" cy="7" r="4"/>
          <path d="M23 21v-2a4 4 0 00-3-3.87"/>
          <path d="M16 3.13a4 4 0 010 7.75"/>
        </svg>
      ),
      label: 'Contacts'
    },
    {
      id: 'groups',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="8" r="3.2"/>
          <path d="M6.5 19.5a5.5 5.5 0 0 1 11 0"/>
          <circle cx="4.5" cy="9" r="2"/>
          <path d="M1.5 16.5a3.6 3.6 0 0 1 3-2.4"/>
          <circle cx="19.5" cy="9" r="2"/>
          <path d="M22.5 16.5a3.6 3.6 0 0 0-3-2.4"/>
        </svg>
      ),
      label: 'Groups'
    },
    {
      id: 'calls',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/>
        </svg>
      ),
      label: 'Calls'
    },
    {
      id: 'pricing',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 3v18h18"/>
          <path d="M7 15l4-4 3 2 4-6"/>
        </svg>
      ),
      label: 'Pricing',
      desktopOnly: true
    }
  ];

  const bottomItems = [
    {
      id: 'settings',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="3"/>
          <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z"/>
        </svg>
      ),
      label: 'Settings'
    }
  ];

  const handleNavClick = (itemId) => {
    setShowMoreMenu(false);
    setShowSettingsMenu(false);
    navigate(`/${itemId}`);
  };

  const handleSettingsAction = (action) => {
    setShowMoreMenu(false);
    setShowSettingsMenu(false);
    if (action === 'settings') {
      navigate('/settings');
    } else if (action === 'starred-messages') {
      navigate('/starred-messages');
    } else if (action === 'pricing') {
      navigate('/pricing');
    }
  };

  const handleInstallApp = async () => {
    setShowMoreMenu(false);
    setShowSettingsMenu(false);
    if (canInstall) {
      const outcome = await promptInstall();
      // Some browsers ignore the prompt or it isn't ready; fall back to help.
      if (outcome === null) {
        setShowInstallHelp(true);
      }
    } else {
      // No native prompt available (iOS Safari, etc.) -> show manual steps.
      setShowInstallHelp(true);
    }
  };

  // Show on every device unless the app is already installed/standalone.
  const installMenuItem = !isInstalled ? (
    <button
      type="button"
      className="nav-more-dropdown-item"
      role="menuitem"
      onClick={handleInstallApp}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
      </svg>
      Install app
    </button>
  ) : null;

  const starredMenuItem = (
    <button
      type="button"
      className="nav-more-dropdown-item"
      role="menuitem"
      onClick={() => handleSettingsAction('starred-messages')}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="#f59e0b" stroke="#f59e0b" strokeWidth="1">
        <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
      </svg>
      Starred messages
    </button>
  );

  const settingsMenuItem = (
    <button
      type="button"
      className="nav-more-dropdown-item"
      role="menuitem"
      onClick={() => handleSettingsAction('settings')}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" />
      </svg>
      Settings
    </button>
  );

  useEffect(() => {
    if (!showMoreMenu && !showSettingsMenu) return undefined;

    const handleClickOutside = (event) => {
      if (!event.target.closest('.nav-more-menu') && !event.target.closest('.nav-settings-menu')) {
        setShowMoreMenu(false);
        setShowSettingsMenu(false);
      }
    };

    const timer = window.setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 0);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showMoreMenu, showSettingsMenu]);

  return (
    <div className={`side-navbar ${isMobile && showChat ? 'mobile-hidden' : ''}`}>
      {!isMobile && (
      <div className="nav-profile" onClick={onProfileClick}>
        <div className="nav-profile-avatar-wrap">
          <div className="nav-profile-avatar">
            {user?.profileImage ? (
              <img
                src={`${config.apiUrl}${user.profileImage}`}
                alt={user?.username || ''}
                className="nav-profile-image"
              />
            ) : (
              <div className="nav-profile-placeholder notranslate">
                {user?.username?.charAt(0)?.toUpperCase() || 'U'}
              </div>
            )}
          </div>
          {isPremium && <PremiumBadge size="sm" />}
        </div>
      </div>
      )}

      <nav className="nav-main">
        {navItems.map((item) => (
          <button
            key={item.id}
            className={`nav-item ${item.desktopOnly ? 'desktop-only' : ''} ${activeTab === item.id ? 'active' : ''}`}
            onClick={() => handleNavClick(item.id)}
            title={item.label}
          >
            <div className="nav-item-content">
              {item.icon}
              {item.id === 'contacts' && notificationCount > 0 && (
                <span className="notification-badge">{notificationCount}</span>
              )}
              {item.id === 'groups' && groupNotificationCount > 0 && (
                <span className="notification-badge">{groupNotificationCount}</span>
              )}
            </div>
          </button>
        ))}

        <div className="nav-more-menu mobile-only">
          <button
            type="button"
            className={`nav-item nav-settings-trigger ${showMoreMenu || activeTab === 'settings' || activeTab === 'starred-messages' ? 'active' : ''}`}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setShowMoreMenu((open) => !open);
            }}
            aria-label="Settings menu"
            aria-expanded={showMoreMenu}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3"/>
              <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z"/>
            </svg>
          </button>
          {showMoreMenu && (
            <div
              className="nav-more-dropdown"
              role="menu"
              onClick={(event) => event.stopPropagation()}
            >
              {settingsMenuItem}
              {starredMenuItem}
              {installMenuItem}
              <button type="button" className="nav-more-dropdown-item" role="menuitem" onClick={() => handleSettingsAction('pricing')}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 3v18h18"/>
                  <path d="M7 16l4-5 3 2 4-6"/>
                </svg>
                Pricing
              </button>
            </div>
          )}
        </div>
      </nav>

      <nav className="nav-bottom">
        <div className="nav-settings-menu desktop-only">
          <button
            type="button"
            className={`nav-item nav-settings-trigger ${showSettingsMenu || activeTab === 'settings' || activeTab === 'starred-messages' ? 'active' : ''}`}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setShowSettingsMenu((open) => !open);
              setShowMoreMenu(false);
            }}
            title="Settings"
            aria-label="Settings menu"
            aria-expanded={showSettingsMenu}
          >
            {bottomItems[0].icon}
          </button>
          {showSettingsMenu && (
            <div
              className="nav-more-dropdown nav-settings-dropdown"
              role="menu"
              onClick={(event) => event.stopPropagation()}
            >
              {settingsMenuItem}
              {starredMenuItem}
              {installMenuItem}
            </div>
          )}
        </div>
      </nav>

      {showInstallHelp && (
        <InstallInstructionsModal
          platform={isIOS ? 'ios' : 'other'}
          onClose={() => setShowInstallHelp(false)}
        />
      )}
    </div>
  );
}

export default SideNavbar;
